<template>
    <div class="give-main">
        <button type="button" class="close-btn" aria-label="Close" :disabled="!!assigningId" @click="close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#71717A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </button>

        <div class="header">
            <div class="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <rect width="48" height="48" rx="24" fill="#E8F5E8" />
                    <path d="M20 24L22.5 26.5L28 21M16 32H32C33.1046 32 34 31.1046 34 30V18C34 16.8954 33.1046 16 32 16H16C14.8954 16 14 16.8954 14 18V30C14 31.1046 14.8954 32 16 32Z" stroke="#22C55E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </div>
            <h2 class="title">Assign a license to {{ contactName }}</h2>
            <p class="subtitle">
                You have {{ availableLicenses.length }} license{{ availableLicenses.length === 1 ? '' : 's' }} available.
                Pick one to assign now, or purchase a new one.
            </p>
        </div>

        <div class="divider" />

        <div v-if="availableLicenses.length" class="license-list">
            <button
                v-for="license in pagedLicenses"
                :key="license.id"
                type="button"
                class="license-row"
                :disabled="!!assigningId"
                @click="assign(license)"
            >
                <div class="license-info">
                    <span class="license-plan">{{ planName(license) }}</span>
                    <span class="license-meta">{{ termLabel(license.term) }}</span>
                </div>
                <span v-if="assigningId === license.id" class="spinner" />
                <span v-else class="assign-cta">Assign</span>
            </button>

            <div v-if="totalPages > 1" class="pager">
                <button type="button" class="pager-btn" :disabled="page === 1 || !!assigningId" @click="page--">Previous</button>
                <span class="pager-status">Page {{ page }} of {{ totalPages }}</span>
                <button type="button" class="pager-btn" :disabled="page === totalPages || !!assigningId" @click="page++">Next</button>
            </div>
        </div>
        <p v-else class="empty-text">You don't have any available licenses. Purchase one to assign to {{ contactName }}.</p>

        <div class="footer">
            <PrimeButton
                variant="secondary"
                class="p-btn-root_secondary purchase-btn"
                label="Purchase a new license"
                :disabled="!!assigningId"
                @click="purchaseNew"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { getDialogRef } from '~/composables/dynamic-dialog';
import { usePersonStore } from '~/stores/person';
import { useContactsStore } from '~/stores/person/contacts';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { LicenseRenewalTermDisplay, LicenseTypeName, type LicenseRenewalTerm } from '~/types/licenses/licenses';
import type { License } from '~/types/person/person';
import { ModalNames } from '~/types/modals';
import { checkoutAssignUrl } from '~/utils/cross-app-urls';

const dialogRef = getDialogRef(ModalNames.GIVE_LICENSE);
const personStore = usePersonStore();
const contactsStore = useContactsStore();
const subscribeStore = useSubscribeStore();
const route = useRoute();

const assigningId = ref<number | null>(null);

const contact = computed(() => dialogRef.value?.data?.contact);
const contactName = computed(() => contact.value?.name ?? 'this family member');

// Any license the user owns that isn't assigned to anyone and hasn't expired — available to give.
const availableLicenses = computed<License[]>(
    () => (personStore.person?.licenses ?? []).filter((l) => !l.assigned_to && !l.expired),
);

// Front-end pagination: show at most 5 licenses per page.
const PAGE_SIZE = 5;
const page = ref(1);
const totalPages = computed(() => Math.max(1, Math.ceil(availableLicenses.value.length / PAGE_SIZE)));
const pagedLicenses = computed(() =>
    availableLicenses.value.slice((page.value - 1) * PAGE_SIZE, page.value * PAGE_SIZE),
);
// Clamp the page if the list shrinks (e.g. after assigning one and the list refreshes).
watch(totalPages, (t) => { if (page.value > t) page.value = t; });

const termLabel = (term: LicenseRenewalTerm) =>
    (LicenseRenewalTermDisplay as Record<number, string>)[term] ?? '';

// Prefer the license's display name; fall back to the type name from its slug (display can be
// empty on some licenses), then the raw slug.
const planName = (license: License) =>
    license.display || (LicenseTypeName as Record<string, string>)[license.slug] || license.slug;

const close = () => {
    if (assigningId.value) return;
    dialogRef.value?.close();
};

const assign = async (license: License) => {
    if (assigningId.value || !contact.value) return;
    assigningId.value = license.id;
    try {
        // Store action assigns, refreshes person data, and shows a success toast.
        await subscribeStore.assignLicense(license.id, contact.value, true);
        await contactsStore.fetchContacts();
        dialogRef.value?.close();
    } catch {
        // Error toast already surfaced by the store; keep the modal open to retry.
    } finally {
        assigningId.value = null;
    }
};

const purchaseNew = () => {
    if (!contact.value) return;
    dialogRef.value?.close();
    // Preserve the originating route (impersonation `?person`, non-/family surfaces) in return_to,
    // matching the GIFT branch in useLicensePurchase rather than hard-coding /family.
    window.location.href = checkoutAssignUrl({ id: contact.value.id, name: contact.value.name }, route.fullPath);
};
</script>

<style lang="scss" scoped>
.give-main {
    @apply relative sm:max-w-xl sm:w-[440px] w-11/12 rounded-lg bg-white mx-auto h-auto overflow-y-auto;
}

.close-btn {
    @apply absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-zinc-100 disabled:opacity-50 z-10;
}

.empty-text {
    @apply px-6 text-gray-600 font-inter text-sm font-normal leading-5;
}

.header {
    @apply flex flex-col p-6 pb-0 items-start gap-1 self-stretch w-full;

    .icon {
        @apply mb-2;
    }
    .title {
        @apply text-gray-900 font-inter text-lg not-italic font-semibold leading-7;
    }
    .subtitle {
        @apply text-gray-600 font-inter text-sm not-italic font-normal leading-5;
    }
}

.divider {
    @apply h-px bg-zinc-300 my-4 mx-6;
}

.license-list {
    @apply flex flex-col gap-2 px-6;
}

.license-row {
    @apply flex items-center justify-between w-full rounded-lg border border-zinc-200 bg-white px-4 py-3 text-left transition;
    @apply hover:border-blue-500 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60;

    .license-info {
        @apply flex flex-col;
    }
    .license-plan {
        @apply text-gray-900 font-inter text-sm font-semibold leading-5 capitalize;
    }
    .license-meta {
        @apply text-gray-500 font-inter text-xs font-normal leading-5;
    }
    .assign-cta {
        @apply text-blue-600 font-inter text-sm font-semibold leading-5;
    }
}

.pager {
    @apply flex items-center justify-between pt-2;

    .pager-btn {
        @apply text-blue-600 font-inter text-sm font-semibold leading-5 transition hover:text-blue-700 disabled:text-zinc-300 disabled:cursor-not-allowed;
    }
    .pager-status {
        @apply text-gray-500 font-inter text-xs font-normal leading-5;
    }
}

.footer {
    @apply flex flex-col justify-center items-stretch self-stretch p-6 pt-6;

    .purchase-btn {
        @apply w-full;
    }
}

.spinner {
    @apply w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin;
}
</style>
